import { useTranslation } from '../../../i18n';
import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Button, Card, Checkbox, Col, DatePicker, Form, Input, InputNumber, Modal, Popconfirm, Progress,
    Row, Select, Space, Spin, Switch, Table, Tag, Typography, theme,
} from 'antd';
import dayjs from 'dayjs';
import { ok, runAction, formatMB } from './utils';
import { formatDateTime } from '../../../utils/date';

const { Text } = Typography;

const ACTION = { throttle: 'Throttle', cut_public: 'Cut public network', shutdown: 'Shut down' };
const LEVEL = { ok: ['Normal', 'success'], warn: ['≥80%', 'warning'], over: ['Over quota', 'error'] };
const RES = [
    { key: 'traffic', label: 'Traffic', fmt: (v) => formatMB(v) },
    { key: 'cpu', label: 'CPU core-hours', fmt: (v) => `${Number(v || 0).toFixed(2)}` },
    { key: 'io', label: 'Disk IO', fmt: (v) => `${Number(v || 0).toFixed(2)} GB` },
];
const THROTTLE_FIELDS = [
    ['net_mbps', 'Network Mbps'], ['cpu_pct', 'CPU %/vCPU'], ['io_read_mbps', 'Read MB/s'],
    ['io_write_mbps', 'Write MB/s'], ['io_read_iops', 'Read IOPS'], ['io_write_iops', 'Write IOPS'],
];
const TASK_STATUS = { 2: ['Queued', 'default'], 3: ['Running', 'processing'], 4: ['Failed', 'error'], 5: ['Active', 'success'] };

/** Admin-only: monthly quotas, overage state, overrides and manual restrictions of one VM. */
const QuotaTab = ({ id, api, isMobile, active, onChanged }) => {
    const { t } = useTranslation();
    const { token } = theme.useToken();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [modal, setModal] = useState(null); // 'add' | 'throttle' | 'limit'
    const [busy, setBusy] = useState(false);
    const [form] = Form.useForm();

    const load = useCallback(async () => {
        setLoading(true);
        const r = await api.getVmQuota(id);
        setLoading(false);
        if (ok(r)) setData(r.data);
    }, [api, id]);

    useEffect(() => { if (active) load(); }, [active, load]);

    const after = (res) => {
        if (res) {
            if (res.data) setData(res.data);
            setModal(null);
            onChanged?.();
        }
    };
    const op = async (name, payload = {}) => {
        setBusy(true);
        after(await runAction(() => api.vmQuotaOp(id, name, payload), t('Saved'), t('Operation failed')));
        setBusy(false);
    };
    const submitModal = async () => {
        const v = await form.validateFields();
        setBusy(true);
        let res;
        if (modal === 'add') {
            res = await runAction(() => api.vmQuotaOp(id, 'add_quota', {
                resource: v.resource, amount: v.resource === 'traffic' ? v.amount * 1024 : v.amount, note: v.note,
            }), t('Saved'), t('Operation failed'));
        } else if (modal === 'throttle') {
            res = await runAction(() => api.vmQuotaOp(id, 'throttle', v), t('Saved'), t('Operation failed'));
        } else {
            res = await runAction(() => api.setVmLimit(id, {
                ...v,
                io: { read_mbps: v.io_read_mbps, write_mbps: v.io_write_mbps, read_iops: v.io_read_iops, write_iops: v.io_write_iops },
                cut_public: v.cut_public ? 1 : 0,
                show_reason: v.show_reason ? 1 : 0,
                expires_at: v.expires_at ? v.expires_at.format('YYYY-MM-DD HH:mm:ss') : '',
            }), t('Active'), t('Operation failed'));
        }
        setBusy(false);
        after(res);
    };

    if (!data) return <div style={{ textAlign: 'center', padding: 40 }}>{loading ? <Spin /> : <Text type="secondary">-</Text>}</div>;
    if (!data.available) return <Alert type="info" message={t('Database not migrated')} />;

    const { levels, config, usage, state, manual } = data;
    const m = data.manual_active ? manual : null;
    const task = data.applied_task;
    const openModal = (name, values) => {
        form.resetFields();
        form.setFieldsValue(values || {});
        setModal(name);
    };

    return (
        <Spin spinning={loading}>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                {!data.agent_ok && <Alert type="warning" showIcon message={t('Node agent is too old; limits can\'t be applied')} />}
                {m && (
                    <Alert
                        type="error"
                        showIcon
                        message={(
                            <Space wrap>
                                <Text strong>{t('Manual restriction')}</Text>
                                {m.cut_public && <Tag color="red">{t('Public network blocked')}</Tag>}
                                {m.net_mbps && <Tag>{`${m.net_mbps} Mbps`}</Tag>}
                                {m.cpu_pct && <Tag>{`CPU ${m.cpu_pct}%`}</Tag>}
                                {Object.entries(m.io || {}).map(([k, v]) => <Tag key={k}>{`${k} ${v}`}</Tag>)}
                                <Text type="secondary">{m.reason}</Text>
                                {m.expires_at && <Text type="secondary">{t('Until {{time}}', { time: formatDateTime(m.expires_at) })}</Text>}
                            </Space>
                        )}
                        action={(
                            <Popconfirm title={t('Lift manual restriction?')} onConfirm={async () => {
                                setBusy(true);
                                after(await runAction(() => api.liftVmLimit(id), t('Lifted'), t('Operation failed')));
                                setBusy(false);
                            }}>
                                <Button size="small" loading={busy}>{t('Lift')}</Button>
                            </Popconfirm>
                        )}
                    />
                )}

                <Card
                    size="small"
                    title={t('Usage this cycle')}
                    extra={(
                        <Space size={4} wrap>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {formatDateTime(usage.since)} → {usage.reset_at ? formatDateTime(usage.reset_at) : '-'}
                            </Text>
                        </Space>
                    )}
                >
                    {RES.map(({ key, label, fmt }) => {
                        const lv = levels[key] || {};
                        const [lt, lc] = LEVEL[lv.level] || LEVEL.ok;
                        const pct = lv.pct != null ? Math.min(100, lv.pct) : 0;
                        return (
                            <Row key={key} gutter={12} align="middle" style={{ marginBottom: 8 }}>
                                <Col xs={24} md={4}><Text strong>{t(label)}</Text></Col>
                                <Col xs={24} md={12}>
                                    <Progress percent={+pct.toFixed(1)} showInfo={lv.limit > 0} size="small"
                                        status={lv.level === 'over' ? 'exception' : 'normal'}
                                        strokeColor={lv.level === 'warn' ? token.colorWarning : undefined} />
                                </Col>
                                <Col xs={24} md={8}>
                                    <Space size={4} wrap>
                                        <Text>{fmt(lv.used)} / {lv.limit > 0 ? fmt(lv.limit) : t('Unlimited')}</Text>
                                        <Tag color={lc}>{t(lt)}</Tag>
                                        <Tag>{t(ACTION[config.action[key]])}</Tag>
                                    </Space>
                                </Col>
                            </Row>
                        );
                    })}
                    {levels.io?.parts && (levels.io.parts.io_read_gb.limit > 0 || levels.io.parts.io_write_gb.limit > 0) && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {t('Read')} {usage.io_read_gb} / {levels.io.parts.io_read_gb.limit || '-'} GB · {t('Write')} {usage.io_write_gb} / {levels.io.parts.io_write_gb.limit || '-'} GB
                        </Text>
                    )}
                </Card>

                <Card size="small" title={t('Actions')}>
                    <Space wrap>
                        <Popconfirm title={t('Reset this cycle\'s usage to zero?')} onConfirm={() => op('reset_usage', { resources: ['traffic', 'cpu', 'io'] })}>
                            <Button loading={busy}>{t('Reset usage')}</Button>
                        </Popconfirm>
                        <Button onClick={() => openModal('add', { resource: 'traffic' })}>{t('Add quota')}</Button>
                        <Button onClick={() => openModal('throttle', { ...config.throttle, ...(state.throttle || {}) })}>{t('Overage throttle settings')}</Button>
                        <Button danger onClick={() => openModal('limit', { cut_public: false, show_reason: false })}>{t('Throttle / block public network')}</Button>
                        <Space size={4}>
                            <Switch checked={!!state.exempt} disabled={data.exempt_uid} onChange={(v) => op('exempt', { exempt: v ? 1 : 0 })} />
                            <Text>{t('Exempt')}</Text>
                            {data.exempt_uid && <Tag>{t('Account exempt')}</Tag>}
                        </Space>
                        <Button type="link" onClick={() => op('repush')}>{t('Re-push')}</Button>
                    </Space>
                    {task && (
                        <div style={{ marginTop: 8 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {t('Last push')} #{task.id} <Tag color={(TASK_STATUS[task.status] || [])[1]}>{t((TASK_STATUS[task.status] || [task.status])[0])}</Tag>
                                {String(task.status) === '4' && task.msg}
                            </Text>
                        </div>
                    )}
                </Card>

                {(data.packs?.length > 0 || manual?.history?.length > 0) && (
                    <Row gutter={16}>
                        {data.packs?.length > 0 && (
                            <Col xs={24} lg={12}>
                                <Card size="small" title={t('Added this cycle')}>
                                    <Table size="small" pagination={false} rowKey="id" dataSource={data.packs} columns={[
                                        { title: t('Resources'), dataIndex: 'resource', render: (v) => t((RES.find((r) => r.key === v) || {}).label || v) },
                                        { title: t('Quantity'), dataIndex: 'amount', render: (v, r) => (r.resource === 'traffic' ? formatMB(v) : v) },
                                        { title: t('Source'), dataIndex: 'source', render: (v) => (v === 'invoice' ? t('Buy') : t('Admin')) },
                                        { title: t('Time'), dataIndex: 'created_at', render: (v) => formatDateTime(v) },
                                    ]} />
                                </Card>
                            </Col>
                        )}
                        {manual?.history?.length > 0 && (
                            <Col xs={24} lg={12}>
                                <Card size="small" title={t('Restriction history')}>
                                    <Table size="small" pagination={false} rowKey={(r, i) => i} dataSource={[...manual.history].reverse().slice(0, 10)} columns={[
                                        { title: t('Time'), dataIndex: 'at', render: (v) => formatDateTime(v) },
                                        { title: t('Actions'), dataIndex: 'op', render: (v) => ({ apply: t('Limits'), lift: t('Lift'), expired: t('Expires') }[v] || v) },
                                        { title: t('Reason'), dataIndex: 'reason', ellipsis: true },
                                        { title: t('Admin'), dataIndex: 'by', render: (v) => (v ? `#${v}` : '-') },
                                    ]} />
                                </Card>
                            </Col>
                        )}
                    </Row>
                )}
            </Space>

            <Modal
                open={!!modal}
                title={{ add: t('Add quota (this cycle)'), throttle: t('Overage throttle settings'), limit: t('Throttle / block public network') }[modal]}
                onCancel={() => setModal(null)}
                onOk={submitModal}
                confirmLoading={busy}
                width={isMobile ? '95%' : 560}
                destroyOnClose
            >
                <Form form={form} layout="vertical">
                    {modal === 'add' && (
                        <Row gutter={12}>
                            <Col span={10}>
                                <Form.Item name="resource" label={t('Resources')}>
                                    <Select options={[
                                        { value: 'traffic', label: t('Traffic (GB)') },
                                        { value: 'cpu', label: t('CPU core-hours') },
                                        { value: 'io', label: t('Disk IO (GB)') },
                                    ]} />
                                </Form.Item>
                            </Col>
                            <Col span={14}>
                                <Form.Item name="amount" label={t('Quantity')} rules={[{ required: true }]}>
                                    <InputNumber min={0.01} style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item name="note" label={t('Notes')}><Input maxLength={200} /></Form.Item>
                            </Col>
                        </Row>
                    )}
                    {modal === 'throttle' && (
                        <Row gutter={12}>
                            {THROTTLE_FIELDS.map(([k, label]) => (
                                <Col span={8} key={k}>
                                    <Form.Item name={k} label={t(label)}><InputNumber min={1} max={k === 'cpu_pct' ? 100 : undefined} style={{ width: '100%' }} /></Form.Item>
                                </Col>
                            ))}
                        </Row>
                    )}
                    {modal === 'limit' && (
                        <>
                            <Row gutter={12}>
                                {THROTTLE_FIELDS.map(([k, label]) => (
                                    <Col span={8} key={k}>
                                        <Form.Item name={k} label={t(label)}><InputNumber min={1} max={k === 'cpu_pct' ? 100 : undefined} style={{ width: '100%' }} placeholder={t('Unlimited')} /></Form.Item>
                                    </Col>
                                ))}
                            </Row>
                            <Form.Item name="cut_public" valuePropName="checked">
                                <Checkbox>{t('Block public network (keep VPC and VNC)')}</Checkbox>
                            </Form.Item>
                            <Form.Item name="reason" label={t('Reason')} rules={[{ required: true, message: t('Required') }]}>
                                <Input maxLength={255} />
                            </Form.Item>
                            <Row gutter={12}>
                                <Col span={14}>
                                    <Form.Item name="expires_at" label={t('Auto-lift at')}>
                                        <DatePicker showTime style={{ width: '100%' }} disabledDate={(d) => d && d < dayjs().startOf('day')} />
                                    </Form.Item>
                                </Col>
                                <Col span={10}>
                                    <Form.Item name="show_reason" label={t('Show reason to user')} valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </>
                    )}
                </Form>
            </Modal>
        </Spin>
    );
};

export default QuotaTab;
